import 'amazon-connect-streams';
import 'amazon-connect-chatjs';
import { Backdrop, Box, CircularProgress } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { useTelephonyContext } from '../../contexts/TelephonyContext';
import { useChatContext } from '../../contexts/ChatContext';
import AuthenticationExpired from '../AuthenticationExpired';
import { FeatureFlag, HEADER_HEIGHT, TOTAL_HEIGHT_WITH_TELEPHONY } from '../constants';
import Header from './Header/Header';
import PageContainer from './Pages/PageContainer';
import SidePanel from './Sidepanel/SidePanel';
import TelephonyStatusBar from './TelephonyStatusBar/TelephonyStatusBar';
import { useCassieWebsocket } from '../CassieContainer/websocket/CassieWebsocketContext';
import { useFeatureFlags } from '../ToolkitPanel/useFeatureFlags';
import { BackdropState, CatoContainerProps } from '../../models/CatoContainerTypes';
import { useSnackbar } from '../../utils/hooks/useSnackbar';
import { Snackbar as FreddySnackbar } from '@coolblue-development/freddy';
import {
  useChatContactHandlers,
  useChatNotificationHandler,
  useTelephonyContactHandlers,
  useChatLifecycle,
  useConnectInitialization,
  useEmailContactHandlers,
  useInitialChatContacts,
  useContactViewing
} from './hooks';

function CatoContainer({ instanceURL, samURL, region, onViewContact }: CatoContainerProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const [backdropState, setBackdropState] = useState(BackdropState.Loading);
  const { telephonyStatusBarOpen } = useTelephonyContext();
  const { selectedChat, chatContacts } = useChatContext();

  const selectedChatRef = useRef(selectedChat);
  const chatContactsRef = useRef(chatContacts);

  const chatNotificationAudio = useMemo(
    () => new Audio(`${import.meta.env.VITE_CATO_URL ?? ''}/chatNotification.mp3`),
    []
  );

  const { invoke } = useCassieWebsocket();
  const { isFeatureFlagEnabled } = useFeatureFlags();
  const isNotifyChatMessageEnabled = isFeatureFlagEnabled(FeatureFlag.NOTIFY_CHAT_MESSAGE_CASSIE);
  const { snackbar, show, hideSnackbar } = useSnackbar();

  const containerHeight = telephonyStatusBarOpen
    ? `calc(100vh - ${TOTAL_HEIGHT_WITH_TELEPHONY}px)`
    : `calc(100vh - ${HEADER_HEIGHT}px)`;

  const showBackdrop = backdropState !== BackdropState.None;
  const isLoading = backdropState === BackdropState.Loading;
  const isAuthExpired = backdropState === BackdropState.AuthenticationExpired;

  selectedChatRef.current = selectedChat;
  chatContactsRef.current = chatContacts;

  useConnectInitialization({ containerRef, instanceURL, samURL, region, setBackdropState });
  useInitialChatContacts();
  useChatLifecycle({ selectedChatRef, chatContactsRef, onViewContact });
  useChatContactHandlers({ chatNotificationAudio, selectedChatRef });
  useTelephonyContactHandlers({ onViewContact });
  useEmailContactHandlers({ onViewContact, onError: message => show(message, 'error') });
  useChatNotificationHandler({ isNotifyChatMessageEnabled, invoke });
  useContactViewing({ onViewContact });

  return (
    <div style={{ position: 'relative' }}>
      <Header instanceUrl={instanceURL} />

      {telephonyStatusBarOpen && <TelephonyStatusBar />}

      <Box
        sx={{
          display: 'flex',
          background: 'white',
          height: containerHeight
        }}
      >
        <SidePanel />
        <PageContainer containerRef={containerRef} />
      </Box>

      <Backdrop sx={{ background: '#F2F7FC', position: 'absolute' }} open={showBackdrop}>
        {isLoading && <CircularProgress />}
        {isAuthExpired && <AuthenticationExpired />}
      </Backdrop>

      <FreddySnackbar
        open={snackbar.open}
        title={snackbar.title}
        intent={snackbar.intent}
        duration={snackbar.duration}
        onClose={hideSnackbar}
        placement="bottom"
      />
    </div>
  );
}

export default CatoContainer;
